/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-empty */
import {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosInstance,
} from 'axios';
import Bugsnag from '../bugsnag';
import { InitializeTypes } from '../../types';
import EventsHelper from '../events/EventsHelper';
import { Logger } from '../logger';
import { Bundle } from '../../util';
import { isNull, isValue } from '../../helpers';

interface ICustomAxiosRequestConfig extends AxiosRequestConfig {
  metadata?: any;
}

const initMessage = '\n'.repeat(2).concat('-'.repeat(64), '\n'.repeat(2));
const serviceEndpoint = 'service-endpoint';

export const configureAxiosIntLogger = (
  instance: AxiosInstance,
  config: InitializeTypes,
) => {
  if (config.debug) {
    // eslint-disable-next-line no-console
    console.debug(`AxiosInterceptor.ts => configureAxiosIntLogger`);
  }
  const isLoggerService = config.service.logger;
  const isLoggerEvents = config.channel === 'WEB_FLOW' ? true : false;

  try {
    const idInterceptorRequest = Bundle.getValue('idInterceptorAxiosRequest');
    if (isValue(idInterceptorRequest)) {
      instance.interceptors.request.eject(idInterceptorRequest);
    }
  } catch (error) {
    Logger.error(error);
  }

  try {
    const idInterceptorAxiosRequest = instance.interceptors.request.use(
      (request: ICustomAxiosRequestConfig) => {
        request.metadata = { requestStartTime: new Date() };
        prepareLogRequest(config, request, isLoggerService, isLoggerEvents);
        return request;
      },
      (error: any[] | undefined) => {
        Logger.error('Request Send Error : ', error);

        return Promise.reject(error);
      },
    );
    Bundle.setValue('idInterceptorAxiosRequest', idInterceptorAxiosRequest);
  } catch (error) {
    Logger.error(error);
  }

  try {
    const idInterceptorResponse = Bundle.getValue('idInterceptorAxiosResponse');
    if (isValue(idInterceptorResponse)) {
      instance.interceptors.request.eject(idInterceptorResponse);
    }
  } catch (error) {
    Logger.error(error);
  }

  try {
    const idInterceptorAxiosResponse = instance.interceptors.response.use(
      (response: AxiosResponse) => {
        const timeStart = new Date();
        prepareLogResponse({
          config,
          response,
          timeStart,
          isLoggerService,
          isLoggerEvents,
        });
        return response;
      },
      (error: any) => {
        // const timeStart = error.config.metadata.requestStartTime;
        // const timeStart = error.config.headers['request-startTime'];
        const timeStart = new Date();
        prepareLogResponse({
          config,
          error,
          timeStart,
          isLoggerService,
          isLoggerEvents,
        });
        return Promise.reject(error);
      },
    );
    Bundle.setValue('idInterceptorAxiosResponse', idInterceptorAxiosResponse);
  } catch (error) {
    Logger.error(error);
  }
};

const prepareLogRequest = (
  config: InitializeTypes,
  requetConfig: AxiosRequestConfig,
  isLoggerService: boolean,
  isLoggerEvents: boolean,
) => {
  if (config.debug) {
    // eslint-disable-next-line no-console
    console.debug(`AxiosInterceptor.ts => prepareLogRequest`);
  }
  let msg = initMessage;
  const { data, headers, method, url, params } = requetConfig;

  msg += '[Axios Request]   0ms \n\n';

  msg += method ? method.toUpperCase() : '';

  let newUrl = url;
  if (!isNull(params)) {
    const searchParams = prepareUrlParams(params);
    newUrl += searchParams;
  }
  msg += newUrl ? ` ${newUrl}\n` : '';

  if (headers) {
    msg += 'Headers:';
    for (const key in headers) {
      if (typeof headers[key] === 'string') {
        msg += `\n  ${key}: ${headers[key]}`;
      }
    }
  }

  if (data) {
    const dataStr = JSON.stringify(data, null, 3);
    msg += `\nBody: \n${dataStr}\n`;
  }

  if (isLoggerService) {
    Logger.log(msg);
  }

  if (isLoggerEvents) {
    const baseUrl = Bundle.getValue(serviceEndpoint);
    const path = String(url).replace(baseUrl, '');
    const formatedEvent = { path, message: msg.toString() };
    EventsHelper.emitMessage('api-request', formatedEvent);
  }
};

const prepareLogResponse = (logParams: {
  config: InitializeTypes;
  response?: AxiosResponse;
  error?: AxiosError;
  timeStart: any;
  isLoggerService: boolean;
  isLoggerEvents: boolean;
}) => {
  const { config, timeStart, isLoggerService, isLoggerEvents } = logParams;

  if (config.debug) {
    // eslint-disable-next-line no-console
    console.debug(`AxiosInterceptor.ts => prepareLogResponse`);
  }
  const time = calculateTime(timeStart);
  let msg = '\n';
  // '\n----------------------------------------------------------------\n\n';

  let mMethod, mUrl, mParams, mResponse, mMessage;
  if (logParams.response) {
    const {
      config: { method, url, params },
    } = logParams.response;
    mResponse = logParams.response;
    mMethod = method;
    mUrl = url;
    mParams = params;
  } else if (logParams.error) {
    const {
      config: { url, method, params },
      response,
      message,
    } = logParams.error;
    mResponse = response;
    mMethod = method;
    mUrl = url;
    mParams = params;
    mMessage = message;
  }

  const isMock =
    mResponse &&
    mResponse.headers &&
    mResponse.headers.mockEnabled &&
    mResponse.headers.mockEnabled === true;

  msg += isMock
    ? '***---***---***---***  MOCK ENABLED  ***---***---***---***\n\n'
    : '';

  msg += `[Axios Response Success] ${time} ms\n\n`;

  msg += mMethod ? `${mMethod.toUpperCase()} ` : '';

  let newUrl = mUrl;
  if (!isNull(mParams)) {
    const searchParams = prepareUrlParams(mParams);
    newUrl += searchParams;
  }
  msg += newUrl ? `${newUrl}\n` : '';

  let dataStr = ';';
  if (mResponse) {
    msg += mResponse.status ? `  Status: ${mResponse.status.toString()}\n` : '';
    msg += mResponse.statusText
      ? `  StatusText: ${mResponse.statusText}\n`
      : '';

    if (mResponse.headers) {
      msg += 'Headers:';
      for (const key in mResponse.headers) {
        msg += `\n  ${key}: ${mResponse.headers[key]}`;
      }
    }

    if (mResponse.data) {
      if (typeof mResponse.data !== 'object') {
        const json = isJsonValid(mResponse.data);
        if (json != null) {
          dataStr = JSON.stringify(json, null, 3);
        }
      } else {
        dataStr = JSON.stringify(mResponse.data, null, 3);
      }
      msg += `\nBody: \n${dataStr}\n`;
    }
  }

  if (mMessage) {
    msg += `\nMessage error: \n\n${mMessage}\n\n`;
  }

  msg += '----------------------------------------------------------------\n';
  if (isLoggerEvents) {
    const baseUrl = Bundle.getValue(serviceEndpoint);
    const path = String(mUrl).replace(baseUrl, '');
    const formatedEvent = { path, message: msg.toString() };
    EventsHelper.emitMessage('api-response', formatedEvent);
  }
  if (isLoggerService) {
    Logger.log(msg);
  }

  if (!isMock && logParams.error && isValue(config.bugSnagClient)) {
    generateErroToBugsnag(logParams.error, dataStr, config.bugSnagClient);
  }
};

const generateErroToBugsnag = (
  error: AxiosError,
  responseStr: string,
  bugnagClient: any,
) => {
  const environment = Bundle.getValue('service-environment');
  const {
    config: { data, url, method },
    response,
  } = error;

  const requestStr = data ? data : '';
  const status = response && response.status ? response.status : 999;
  const urlAux = url ? url : '';
  const methodAux = method ? method : '';
  Bugsnag.sendErrorApi({
    environment,
    endpoint: urlAux,
    status,
    method: methodAux.toUpperCase(),
    request: requestStr,
    response: responseStr,
    bugsnagClient: bugnagClient,
  });
};

const isJsonValid = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
};

export const prepareUrlParams = (params: any) => {
  let paramsStr = '';
  let isItemZero = true;
  let entries;

  if (typeof params.entries === 'function') {
    entries = params.entries();
  } else {
    entries = Object.entries(params);
  }

  for (const [key, value] of entries) {
    if (!isItemZero) {
      paramsStr += `&${key}=${value}`;
    } else {
      isItemZero = false;
      paramsStr += `?${key}=${value}`;
    }
  }

  return paramsStr;
};

const calculateTime = (timeStart: Date) => {
  let milliseconds = 0;
  try {
    const end = new Date();
    milliseconds = end.getTime() - timeStart.getTime();
  } catch (error) {
    Logger.info(error);
  }
  return milliseconds;
};
